import React, { useEffect, useMemo, useRef, useState } from 'react';
import TournamentDraftWizard from './TournamentDraftWizard';
import TournamentParticipantsPanel from './TournamentParticipantsPanel';
import TournamentDiscoveryMap from './TournamentDiscoveryMap';
import { usePaginatedQuery } from '../../hooks/usePaginatedQuery';
import { ShareIcon } from '../icons';
import type {
  Tournament,
  TournamentLeaderboardEntry,
  TournamentParticipant,
  TournamentPlatformConfig,
  TournamentSubmission,
  UserProfile,
} from '../../types';
import {
  adminSetTournamentRegistration,
  calculateTournamentStandings,
  finalizeTournamentResults,
  repairTournamentResults,
  resolveTournamentParticipantDispute,
  reviewTournamentSubmission,
  settleTournamentPayouts,
  markTournamentResultsSeen,
  getPublicTournamentHistoryQuery,
  getTournamentById,
  submitTournamentForApproval,
  subscribeMyTournamentSubmissions,
  subscribeTournamentAcceptedSubmissions,
  subscribeTournamentLeaderboard,
  subscribeTournamentParticipant,
  subscribeTournamentParticipants,
  subscribeTournamentSubmissionsForReview,
  withdrawTournamentSubmission,
} from '../../services/tournaments';
import {
  DEFAULT_TOURNAMENT_DISCOVERY_FILTERS,
  TOURNAMENT_DISCOVERY_PREFS_EVENT,
  isTournamentDiscoverableNow,
  isWorldwideTournament,
  loadTournamentDiscoveryPreferences,
  saveTournamentDiscoveryPreferences,
  tournamentPassesDiscoveryFilters,
  type TournamentDiscoveryFilters,
  type TournamentDiscoveryPreferences,
} from '../../utils/tournamentDiscovery';
import { buildTournamentShareUrl, share } from '../../utils/share';
import { getRoddoUiTokens } from '../../constants/theme';
import { getRoddoPageTone } from '../../constants/pageTones';

const PUBLIC_STATUSES = new Set([
  'approved',
  'registration_open',
  'active',
  'live',
  'submissions_closed',
  'under_review',
  'provisional_results',
  'finalized',
  'completed',
  'rejected',
  'cancelled',
  'archived',
]);

const formatTournamentDate = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value)
    ? new Date(value).toLocaleString()
    : '—';

const compactNumber = (value: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value);

const statusLabel = (tournament: Tournament, now = Date.now()) => {
  const status = String(tournament.status || 'draft');
  const approval = String(tournament.approvalStatus || 'not_submitted');
  const registrationClosesAt = Number(tournament.registrationClosesAt || 0);

  if (approval === 'changes_requested') return 'Changes requested';
  if (approval === 'pending' || status === 'pending_approval') return 'Under review';
  if (status === 'approved') return 'Approved';
  if (status === 'registration_open') {
    return registrationClosesAt > 0 && registrationClosesAt <= now
      ? 'Registration closed'
      : 'Registration open';
  }
  if (status === 'active' || status === 'live') return 'Live';
  if (status === 'submissions_closed') return 'Submissions closed';
  if (status === 'under_review') return 'Results review';
  if (status === 'provisional_results') return 'Provisional';
  if (status === 'finalized' || status === 'completed') return 'Final';
  if (status === 'cancelled') return 'Cancelled';
  if (status === 'archived') return 'Archived';
  if (status === 'rejected' || approval === 'rejected') return 'Rejected';
  return 'Draft';
};

const statusTone = (tournament: Tournament) => {
  const status = String(tournament.status || 'draft');
  // Semantic tournament palette:
  // live/active = green, registration = yellow, finished = gold.
  if (['active', 'live'].includes(status)) return 'border border-emerald-500/45 bg-emerald-400/24 text-emerald-900 shadow-[0_3px_10px_rgba(16,185,129,.10)] dark:border-emerald-300/34 dark:bg-emerald-400/22 dark:text-[#8CF0CB]';
  if (status === 'registration_open') return 'border border-yellow-400/55 bg-yellow-300/32 text-yellow-950 shadow-[0_3px_10px_rgba(234,179,8,.10)] dark:border-yellow-300/34 dark:bg-yellow-400/22 dark:text-[#FFE681]';
  if (['finalized', 'completed'].includes(status)) return 'border border-amber-500/48 bg-amber-400/28 text-[#7A4700] shadow-[0_3px_10px_rgba(217,119,6,.10)] dark:border-amber-300/34 dark:bg-amber-400/22 dark:text-[#F6C85F]';
  if (['cancelled', 'rejected'].includes(status)) return 'border border-red-400/28 bg-red-400/14 text-red-700 dark:border-red-300/22 dark:bg-red-400/12 dark:text-red-200';
  return 'border border-black/[.06] bg-black/[.035] text-slate-700 dark:border-white/[.08] dark:bg-white/[.06] dark:text-slate-200';
};

const statusToneOnCover = (tournament: Tournament) => {
  const status = String(tournament.status || 'draft');
  if (['active', 'live'].includes(status)) return 'border border-emerald-200/60 bg-emerald-500/94 text-white shadow-[0_4px_16px_rgba(16,185,129,.30)]';
  if (status === 'registration_open') return 'border border-yellow-100/65 bg-yellow-400/96 text-[#2F2500] shadow-[0_4px_16px_rgba(234,179,8,.30)]';
  if (['finalized', 'completed'].includes(status)) return 'border border-amber-100/60 bg-amber-500/94 text-white shadow-[0_4px_16px_rgba(217,119,6,.28)]';
  if (['cancelled', 'rejected'].includes(status)) return 'border border-red-200/40 bg-red-500/84 text-white shadow-[0_4px_14px_rgba(220,38,38,.20)]';
  return 'border border-white/15 bg-black/45 text-white shadow-[inset_0_1px_0_rgba(255,255,255,.07)]';
};

const countdownLabel = (tournament: Tournament, now: number) => {
  const status = String(tournament.status || '');
  let target = 0;
  let prefix = '';

  if (['active', 'live'].includes(status)) {
    target = Number(tournament.endDate || 0);
    prefix = 'Ends';
  } else if (status === 'registration_open') {
    target = Number(tournament.registrationClosesAt || 0);
    prefix = 'Registration closes';
  } else {
    target = Number(tournament.startDate || 0);
    prefix = 'Starts';
  }

  if (!target) return '';
  const delta = target - now;
  if (delta <= 0) return `${prefix} now`;

  const minutes = Math.floor(delta / 60_000);
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes % (60 * 24)) / 60);
  const mins = minutes % 60;

  if (days > 0) return `${prefix} in ${days}d ${hours}h`;
  if (hours > 0) return `${prefix} in ${hours}h ${mins}m`;
  return `${prefix} in ${Math.max(1, mins)}m`;
};

const compactCountdownDuration = (target: number, now: number) => {
  if (!Number.isFinite(target) || target <= 0) return '';
  const delta = target - now;
  if (delta <= 0) return 'now';
  const minutes = Math.max(1, Math.floor(delta / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const tournamentCardCountdown = (tournament: Tournament, now: number) => {
  const status = String(tournament.status || '').toLowerCase();
  if (['finalized', 'completed', 'cancelled', 'archived', 'rejected'].includes(status)) return '';
  if (['active', 'live'].includes(status)) {
    const value = compactCountdownDuration(Number(tournament.endDate || 0), now);
    return value ? `Ends · ${value}` : '';
  }
  const closesAt = Number(tournament.registrationClosesAt || 0);
  if (status === 'registration_open' && closesAt > now) {
    const value = compactCountdownDuration(closesAt, now);
    return value ? `Closes · ${value}` : '';
  }
  const value = compactCountdownDuration(Number(tournament.startDate || 0), now);
  return value ? `Starts · ${value}` : '';
};

const scoringLabel = (tournament: Tournament) => {
  const mode = String(tournament.scoringMode || 'longest');
  const labels: Record<string, string> = {
    longest: 'Longest Fish',
    heaviest: 'Heaviest Fish',
    best_3: 'Best 3',
    best_5: 'Best 5',
    most_catches: 'Most Catches',
    species_count: 'Species Count',
    total_length: 'Total Length',
    checklist: 'Checklist',
    team_total: 'Team Total',
    roddo_points: 'Scoring unavailable',
  };
  return labels[mode] || mode.replaceAll('_', ' ');
};

const placementMark = (placement: number) => {
  if (placement === 1) return '🥇';
  if (placement === 2) return '🥈';
  if (placement === 3) return '🥉';
  return `#${placement}`;
};

type LiveStanding = {
  id: string;
  placement: number;
  participantUid: string | null;
  participantName: string;
  teamName?: string | null;
  score: number;
  scoreUnit: string;
  acceptedCatchCount: number;
  speciesCount: number;
};

const liveScoreUnit = (tournament: Tournament) => {
  const scoringMode = String(tournament.scoringMode || 'longest');
  if (scoringMode === 'most_catches') return 'catches';
  if (['species_count', 'checklist'].includes(scoringMode)) return 'species';
  if (scoringMode === 'roddo_points') return 'points';
  return String(
    tournament.measurementUnit ||
      (scoringMode === 'heaviest' ? 'lb' : 'in'),
  );
};

const buildLiveStandings = (
  tournament: Tournament,
  submissions: TournamentSubmission[],
): LiveStanding[] => {
  const scoringMode = String(tournament.scoringMode || 'longest');

  // Roddo Points intentionally stays fail-closed until the formula exists.
  if (scoringMode === 'roddo_points') return [];

  const accepted = submissions.filter(
    (submission) => submission.status === 'accepted',
  );

  const useTeam = scoringMode === 'team_total';
  const groups = new Map<string, TournamentSubmission[]>();

  for (const submission of accepted) {
    const participantUid = String(submission.participantUid || '').trim();
    const teamId = String(submission.teamId || '').trim();
    const key = useTeam
      ? teamId
        ? `team:${teamId}`
        : ''
      : participantUid;

    if (!key) continue;
    const bucket = groups.get(key) || [];
    bucket.push(submission);
    groups.set(key, bucket);
  }

  const rows = [...groups.entries()].map(([key, bucket]) => {
    const first = bucket[0];
    const measurements = bucket
      .map((submission) => Number(submission.measurementValue))
      .filter((value) => Number.isFinite(value) && value > 0)
      .sort((a, b) => b - a);

    const species = new Set(
      bucket
        .map((submission) => String(submission.species || '').trim().toLowerCase())
        .filter(Boolean),
    );

    let score = 0;
    switch (scoringMode) {
      case 'longest':
      case 'heaviest':
        score = measurements[0] || 0;
        break;
      case 'best_3':
        score = measurements.slice(0, 3).reduce((sum, value) => sum + value, 0);
        break;
      case 'best_5':
        score = measurements.slice(0, 5).reduce((sum, value) => sum + value, 0);
        break;
      case 'most_catches':
        score = bucket.length;
        break;
      case 'species_count':
      case 'checklist':
        score = species.size;
        break;
      case 'total_length':
        score = measurements.reduce((sum, value) => sum + value, 0);
        break;
      case 'team_total':
        score = measurements.length
          ? measurements.reduce((sum, value) => sum + value, 0)
          : bucket.length;
        break;
      default:
        score = measurements[0] || bucket.length;
        break;
    }

    return {
      id: key,
      placement: 0,
      participantUid: useTeam ? null : first?.participantUid || null,
      participantName:
        first?.participantName || (useTeam ? 'Tournament team' : 'Roddo Angler'),
      teamName: useTeam ? first?.teamName || 'Tournament team' : null,
      score: Number(score.toFixed(4)),
      scoreUnit: liveScoreUnit(tournament),
      acceptedCatchCount: bucket.length,
      speciesCount: species.size,
    } satisfies LiveStanding;
  });

  rows.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (a.teamName || a.participantName).localeCompare(
      b.teamName || b.participantName,
    );
  });

  let placement = 0;
  let previousScore: number | null = null;
  return rows.map((row, index) => {
    if (previousScore === null || row.score !== previousScore) {
      placement = index + 1;
      previousScore = row.score;
    }
    return { ...row, placement };
  });
};

function Disclosure({
  title,
  summary,
  children,
  defaultOpen = false,
}: {
  title: string;
  summary?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details
      className="group border-t border-black/[.07] py-1 first:border-t-0 dark:border-white/[.08]"
      open={defaultOpen}
    >
      <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 py-3 text-sm font-black [&::-webkit-details-marker]:hidden">
        <span>{title}</span>
        <span className="text-lg opacity-45 transition group-open:rotate-90">›</span>
      </summary>
      {summary ? <p className="-mt-2 pb-2 text-xs font-semibold opacity-55">{summary}</p> : null}
      <div className="pb-4 text-sm font-semibold leading-relaxed opacity-85">{children}</div>
    </details>
  );
}

// Navigation note: onBack is origin-aware upstream in App.tsx.
// When Tournament Dock is entered from Big Blue, it returns to the Big Blue tournament hub.
type Props = {
  userProfile: UserProfile;
  tournaments: Record<string, Tournament>;
  config: TournamentPlatformConfig;
  addToast: (message: string, type?: any) => void;
  initialTournamentId?: string | null;
  onLogTournamentCatch: (tournament: Tournament) => void;
  onBack: () => void;
  onOpenUserProfile?: (userId: string, tournamentId?: string) => void;
  isDarkMode?: boolean;
};

export default function TournamentHubView({
  userProfile,
  tournaments,
  config,
  addToast,
  initialTournamentId,
  onLogTournamentCatch,
  onBack,
  onOpenUserProfile,
  isDarkMode = false,
}: Props) {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState<Tournament | null>(null);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [myParticipant, setMyParticipant] = useState<TournamentParticipant | null>(null);
  const [tournamentParticipants, setTournamentParticipants] = useState<Record<string, TournamentParticipant>>({});
  const [tournamentParticipantsLoaded, setTournamentParticipantsLoaded] = useState(false);
  const [mySubmissions, setMySubmissions] = useState<TournamentSubmission[]>([]);
  const [acceptedSubmissions, setAcceptedSubmissions] = useState<TournamentSubmission[]>([]);
  const [reviewSubmissions, setReviewSubmissions] = useState<TournamentSubmission[]>([]);
  const [leaderboardRows, setLeaderboardRows] = useState<TournamentLeaderboardEntry[]>([]);
  const [submissionBusyKey, setSubmissionBusyKey] = useState<string | null>(null);
  const [resultsBusy, setResultsBusy] = useState<'calculate' | 'finalize' | 'repair' | 'payout' | null>(null);
  const [registrationBusyId, setRegistrationBusyId] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [managementOpen, setManagementOpen] = useState(false);
  const [tournamentMenuOpen, setTournamentMenuOpen] = useState(false);
  const [historyView, setHistoryView] = useState<'current' | 'resolve' | 'past'>('current');
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [discoveryFilters, setDiscoveryFilters] = useState<TournamentDiscoveryFilters>(
    () => loadTournamentDiscoveryPreferences().filters,
  );
  const [discoveryFiltersOpen, setDiscoveryFiltersOpen] = useState(false);
  const [tournamentMapBounds, setTournamentMapBounds] = useState<{
    south: number;
    west: number;
    north: number;
    east: number;
  } | null>(null);

  const initialTournamentOpenedRef = useRef<string | null>(null);
  const autoClosingRegistrationRef = useRef<Set<string>>(new Set());
  const actionLocksRef = useRef<Set<string>>(new Set());

  const acquireActionLock = (key: string) => {
    if (actionLocksRef.current.has(key)) return false;
    actionLocksRef.current.add(key);
    return true;
  };

  const releaseActionLock = (key: string) => {
    actionLocksRef.current.delete(key);
  };

  const isAdmin =
    !!(userProfile as any)?.isAdmin ||
    !!(userProfile as any)?.roles?.includes?.('admin');


  const publicPastBaseQuery = useMemo(
    () => (historyView === 'past' ? getPublicTournamentHistoryQuery() : null),
    [historyView],
  );
  const {
    items: paginatedPublicPastRows,
    isLoading: isPastLoading,
    isLoadingMore: isPastLoadingMore,
    hasMore: hasMorePast,
    loadMore: loadMorePast,
  } = usePaginatedQuery<Tournament>(publicPastBaseQuery, {
    pageSize: 20,
    mapDoc: (snapshot) => ({ ...(snapshot.data() as Tournament), id: snapshot.id }),
    deps: [historyView],
  });

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    saveTournamentDiscoveryPreferences({ filters: discoveryFilters });
  }, [discoveryFilters]);

  useEffect(() => {
    const syncTournamentPrefs = (event: Event) => {
      const detail = (event as CustomEvent<TournamentDiscoveryPreferences>)?.detail;
      const next = detail || loadTournamentDiscoveryPreferences();
      setDiscoveryFilters(next.filters);
    };
    window.addEventListener(TOURNAMENT_DISCOVERY_PREFS_EVENT, syncTournamentPrefs);
    return () => window.removeEventListener(TOURNAMENT_DISCOVERY_PREFS_EVENT, syncTournamentPrefs);
  }, []);

  const closeSelectedTournament = () => {
    // Closing tournament detail is local to Tournament Dock.
    // Leaving the Dock is owned by the Dock-level back button.
    setSelectedTournament(null);
    setManagementOpen(false);
    setTournamentMenuOpen(false);
  };

  useEffect(() => {
    if (!initialTournamentId) return;
    if (initialTournamentOpenedRef.current === initialTournamentId) return;

    const liveTarget = tournaments?.[initialTournamentId];
    if (liveTarget) {
      initialTournamentOpenedRef.current = initialTournamentId;
      setSelectedTournament(liveTarget);
      return;
    }

    let cancelled = false;
    void getTournamentById(initialTournamentId)
      .then((target) => {
        if (cancelled || !target) return;
        initialTournamentOpenedRef.current = initialTournamentId;
        setSelectedTournament(target);
      })
      .catch((error) => {
        if (!cancelled) console.warn('[TournamentHubView] historical tournament lookup failed', error);
      });

    return () => {
      cancelled = true;
    };
  }, [initialTournamentId, tournaments]);

  useEffect(() => {
    if (!selectedTournament?.id) return;
    const fresh = tournaments?.[selectedTournament.id];
    if (!fresh) return;
    setSelectedTournament((current) => (current?.id === fresh.id ? fresh : current));
  }, [tournaments, selectedTournament?.id]);

  useEffect(() => {
    if (!selectedTournament?.id || !userProfile.uid) {
      setMyParticipant(null);
      return;
    }
    return subscribeTournamentParticipant(selectedTournament.id, userProfile.uid, setMyParticipant);
  }, [selectedTournament?.id, userProfile.uid]);

  useEffect(() => {
    if (!selectedTournament?.id || myParticipant?.status !== 'approved') return;
    const status = String(selectedTournament.status || '').toLowerCase();
    if (!['finalized', 'completed'].includes(status)) return;
    markTournamentResultsSeen(userProfile.uid, selectedTournament.id);
  }, [selectedTournament?.id, selectedTournament?.status, myParticipant?.status, userProfile.uid]);

  useEffect(() => {
    setTournamentParticipantsLoaded(false);
    if (!selectedTournament?.id) {
      setTournamentParticipants({});
      return;
    }
    return subscribeTournamentParticipants(selectedTournament.id, (participants) => {
      setTournamentParticipants(participants);
      setTournamentParticipantsLoaded(true);
    });
  }, [selectedTournament?.id]);

  useEffect(() => {
    if (!selectedTournament?.id || !userProfile.uid) {
      setMySubmissions([]);
      return;
    }
    return subscribeMyTournamentSubmissions(selectedTournament.id, userProfile.uid, setMySubmissions);
  }, [selectedTournament?.id, userProfile.uid]);

  useEffect(() => {
    if (!selectedTournament?.id) {
      setAcceptedSubmissions([]);
      return;
    }
    const status = String(selectedTournament.status || '');
    if (!['active', 'live', 'submissions_closed', 'under_review', 'provisional_results', 'finalized', 'completed'].includes(status)) {
      setAcceptedSubmissions([]);
      return;
    }
    return subscribeTournamentAcceptedSubmissions(selectedTournament.id, setAcceptedSubmissions);
  }, [selectedTournament?.id, selectedTournament?.status]);

  useEffect(() => {
    if (!selectedTournament?.id) {
      setReviewSubmissions([]);
      return;
    }
  
    const canRead =
      selectedTournament.directorUid === userProfile.uid ||
      isAdmin;
  
    if (!canRead) {
      setReviewSubmissions([]);
      return;
    }
  
    return subscribeTournamentSubmissionsForReview(
      selectedTournament.id,
      setReviewSubmissions,
    );
  }, [
    selectedTournament?.id,
    selectedTournament?.directorUid,
    userProfile.uid,
    isAdmin,
  ]);
  
  useEffect(() => {
    if (!selectedTournament?.id) {
      setLeaderboardRows([]);
      return;
    }
    const status = String(selectedTournament.status || '');
    if (!['submissions_closed', 'under_review', 'provisional_results', 'finalized', 'completed'].includes(status)) {
      setLeaderboardRows([]);
      return;
    }
    return subscribeTournamentLeaderboard(selectedTournament.id, setLeaderboardRows);
  }, [selectedTournament?.id, selectedTournament?.status]);

  useEffect(() => {
    if (!selectedTournament) return;

    const compactViewport = !window.matchMedia('(min-width: 1024px)').matches;
    const previousOverflow = document.body.style.overflow;
    if (compactViewport) document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeSelectedTournament();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      if (compactViewport) document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedTournament]);

  const ui = getRoddoUiTokens(isDarkMode);
  const eventTone = getRoddoPageTone('events');
  const tournamentTone = isDarkMode ? eventTone.dark : eventTone.light;

  const allRows = useMemo(
    () =>
      Object.values(tournaments || {})
        .filter((tournament) => tournament.directorUid === userProfile.uid || PUBLIC_STATUSES.has(String(tournament.status || '')))
        .sort((a, b) => {
          const aMine = a.directorUid === userProfile.uid ? 1 : 0;
          const bMine = b.directorUid === userProfile.uid ? 1 : 0;
          if (aMine !== bMine) return bMine - aMine;
          return Number(b.updatedAt || b.startDate || 0) - Number(a.updatedAt || a.startDate || 0);
        }),
    [tournaments, userProfile.uid],
  );

  const currentRows = useMemo(
    () =>
      allRows.filter((tournament) => {
        const status = String(tournament.status || '');
        const approval = String(tournament.approvalStatus || '');
        const endedAt = Number(tournament.endDate || 0);
        if (approval === 'rejected' || status === 'rejected') return false;
        if (['cancelled', 'archived', 'finalized', 'completed'].includes(status)) return false;
        if (endedAt > 0 && endedAt <= clockNow) return false;
        return true;
      }),
    [allRows, clockNow],
  );

  const livePastRows = useMemo(
    () =>
      allRows.filter((tournament) => {
        const status = String(tournament.status || '');
        const approval = String(tournament.approvalStatus || '');
        return (
          approval === 'rejected' ||
          status === 'rejected' ||
          ['cancelled', 'archived', 'finalized', 'completed'].includes(status)
        );
      }),
    [allRows],
  );

  const pastRows = useMemo(() => {
    const merged = new Map<string, Tournament>();
    for (const tournament of paginatedPublicPastRows) {
      const status = String(tournament.status || '');
      const approval = String(tournament.approvalStatus || '');
      const resolved =
        approval === 'rejected' ||
        status === 'rejected' ||
        ['cancelled', 'archived', 'finalized', 'completed'].includes(status);
      if (resolved) merged.set(tournament.id, tournament);
    }
    // Prefer the live/owned copy when the same tournament is present in both sources.
    for (const tournament of livePastRows) merged.set(tournament.id, tournament);
    return [...merged.values()].sort((a, b) =>
      Number(b.endDate || b.startDate || b.updatedAt || 0) -
      Number(a.endDate || a.startDate || a.updatedAt || 0),
    );
  }, [paginatedPublicPastRows, livePastRows]);

  const shellBalance = Math.max(0, Number((userProfile as any)?.shells ?? (userProfile as any)?.shellBalance ?? 0));
  const publicDiscoveryRows = useMemo(
    () => currentRows.filter((tournament) => isTournamentDiscoverableNow(tournament, clockNow)),
    [currentRows, clockNow],
  );
  const hostQueueRows = useMemo(
    () =>
      currentRows
        .filter((tournament) => {
          if (tournament.directorUid !== userProfile.uid) return false;
          const status = String(tournament.status || '');
          const approval = String(tournament.approvalStatus || '');
          return (
            status === 'pending_approval' ||
            approval === 'pending' ||
            (status === 'draft' && ['not_submitted', 'changes_requested'].includes(approval || 'not_submitted'))
          );
        })
        .sort((a, b) => Number(b.updatedAt || b.submittedAt || 0) - Number(a.updatedAt || a.submittedAt || 0)),
    [currentRows, userProfile.uid],
  );

  const hostQueueIds = useMemo(
    () => new Set(hostQueueRows.map((tournament) => tournament.id)),
    [hostQueueRows],
  );

  const resolveQueueRows = useMemo(
    () =>
      allRows
        .filter((tournament) => {
          if (tournament.directorUid !== userProfile.uid) return false;
          if (tournament.verifiedSubmissionsEnabled === false) return false;

          const status = String(tournament.status || '');
          if (['submissions_closed', 'under_review', 'provisional_results'].includes(status)) {
            return true;
          }

          // Defensive fallback for the short window before the lifecycle worker
          // advances a just-ended live tournament to submissions_closed.
          const endedAt = Number(tournament.endDate || 0);
          return ['active', 'live'].includes(status) && endedAt > 0 && endedAt <= clockNow;
        })
        .sort(
          (a, b) =>
            Number(b.endDate || b.updatedAt || 0) -
            Number(a.endDate || a.updatedAt || 0),
        ),
    [allRows, userProfile.uid, clockNow],
  );

  const resolveQueueIds = useMemo(
    () => new Set(resolveQueueRows.map((tournament) => tournament.id)),
    [resolveQueueRows],
  );

  const filteredCurrentRows = useMemo(
    () =>
      currentRows.filter((tournament) => {
        // Drafts, revision requests, and pending-review events live in the
        // dedicated host queue below. Keeping them out of public discovery
        // prevents public-map/filter rules from making a host's own event vanish.
        if (hostQueueIds.has(tournament.id) || resolveQueueIds.has(tournament.id)) return false;

        const isDirector = tournament.directorUid === userProfile.uid;
        if (isDirector && String(tournament.visibility || '') !== 'public') return true;
        if (!tournamentPassesDiscoveryFilters(tournament, discoveryFilters, shellBalance, clockNow)) return false;
        if (!tournamentMapBounds) return true;
        // Worldwide events are eligible everywhere and should never disappear
        // just because the angler searched a physical map area.
        if (isWorldwideTournament(tournament)) return true;
        const lat = Number(tournament?.mapLocation?.lat);
        const lng = Number(tournament?.mapLocation?.lng);
        return Number.isFinite(lat) && Number.isFinite(lng) && lat >= tournamentMapBounds.south && lat <= tournamentMapBounds.north && lng >= tournamentMapBounds.west && lng <= tournamentMapBounds.east;
      }),
    [currentRows, hostQueueIds, resolveQueueIds, userProfile.uid, discoveryFilters, shellBalance, clockNow, tournamentMapBounds],
  );

  const rows =
    historyView === 'past'
      ? pastRows
      : historyView === 'resolve'
        ? resolveQueueRows
        : filteredCurrentRows;
  const filtersActive =
    discoveryFilters.mode !== 'all' ||
    discoveryFilters.freeOnly ||
    discoveryFilters.hideUnaffordable ||
    !!tournamentMapBounds;

  const selectedStatus = String(selectedTournament?.status || '');
  const needsResolution = ['submissions_closed', 'under_review', 'provisional_results'].includes(selectedStatus);

  useEffect(() => {
    if (!selectedTournament?.id) return;
    setManagementOpen(['submissions_closed', 'under_review', 'provisional_results'].includes(String(selectedTournament.status || '')));
  }, [selectedTournament?.id]);
  const selectedHistorical = !!selectedTournament && (
    ['submissions_closed', 'under_review', 'provisional_results', 'finalized', 'completed'].includes(selectedStatus) ||
    (Number(selectedTournament.endDate || 0) > 0 && Number(selectedTournament.endDate || 0) <= clockNow)
  );
  const canManage = !!selectedTournament && (selectedTournament.directorUid === userProfile.uid || isAdmin);
  const adminOverride = !!selectedTournament && isAdmin && selectedTournament.directorUid !== userProfile.uid;
  const canEditDraft = !!selectedTournament && selectedTournament.directorUid === userProfile.uid && selectedStatus === 'draft' && ['not_submitted', 'changes_requested'].includes(String(selectedTournament.approvalStatus || 'not_submitted'));
  const canEditLiveDetails = !!selectedTournament && (selectedTournament.directorUid === userProfile.uid || isAdmin) && ['pending_approval', 'approved', 'registration_open', 'active', 'live'].includes(selectedStatus);
  const canEditTournament = canEditDraft || canEditLiveDetails;
  const disputeWindowEndsAt = Number((selectedTournament as any)?.disputeWindowEndsAt || 0);
  const disputeWindowOpen = !!selectedTournament && selectedStatus === 'provisional_results' && disputeWindowEndsAt > clockNow;
  const canFinalize = canManage && (!disputeWindowOpen || isAdmin);
  const approvedParticipants = Object.values(tournamentParticipants).filter((p) => p.status === 'approved');
  const approvedParticipantCount = approvedParticipants.length;
  const displayApprovedParticipantCount = tournamentParticipantsLoaded
    ? approvedParticipantCount
    : Math.max(0, Number(selectedTournament?.approvedParticipantCount || 0));
  const approvedDuoMemberCounts = approvedParticipants.reduce<Record<string, number>>((counts, participant) => {
    const teamId = String((participant as any).teamId || '').trim();
    if (teamId) counts[teamId] = (counts[teamId] || 0) + 1;
    return counts;
  }, {});
  const approvedDuoCount = Object.values(approvedDuoMemberCounts).filter((count) => count === 2).length;
  const pendingReviewCount = reviewSubmissions.filter((submission) => submission.status === 'submitted').length;

  const prizePayoutLabel = selectedTournament ? (() => {
    const distribution = String(selectedTournament.prizeDistribution || 'winner_take_all');
    const isDuo = selectedTournament.participationMode === 'team';
    if (distribution === 'team_split') return 'Winning Duo splits the pool';
    if (distribution === 'top_three') return 'Top 3 split the pool';
    if (distribution === 'top_five') return 'Top 5 split the pool';
    if (distribution === 'winner_take_all') return isDuo ? 'Winning Duo takes the pool' : 'Winner takes all';
    return distribution.replaceAll('_', ' ');
  })() : '';

  const liveMode = ['active', 'live'].includes(selectedStatus);
  const liveStandings = useMemo(
    () =>
      selectedTournament
        ? buildLiveStandings(selectedTournament, acceptedSubmissions)
        : [],
    [selectedTournament, acceptedSubmissions],
  );

  const displayedStandings: Array<TournamentLeaderboardEntry | LiveStanding> =
    liveMode ? liveStandings : leaderboardRows;

  const myTeamId = String((myParticipant as any)?.teamId || '').trim();
  const myLiveStanding = liveMode
    ? liveStandings.find((entry) =>
        entry.participantUid === userProfile.uid ||
        (!!myTeamId && entry.id === `team:${myTeamId}`),
      ) || null
    : null;

  const isRegisteredCompetitor = myParticipant?.status === 'approved';
  const isRegistrationPending = myParticipant?.status === 'pending';
  const startsInLabel = selectedTournament
    ? countdownLabel({ ...selectedTournament, status: 'approved' } as Tournament, clockNow)
    : '';
  const endsInLabel = selectedTournament
    ? countdownLabel({ ...selectedTournament, status: 'live' } as Tournament, clockNow)
    : '';

  const myAcceptedSubmissions = mySubmissions.filter(
    (submission) => submission.status === 'accepted',
  );
  const myPendingSubmissions = mySubmissions.filter(
    (submission) => submission.status === 'submitted',
  );

  useEffect(() => {
    if (!selectedTournament?.id || selectedTournament.status !== 'registration_open') return;
    const closeAt = Number(selectedTournament.registrationClosesAt || 0);
    if (!closeAt || closeAt > clockNow || autoClosingRegistrationRef.current.has(selectedTournament.id)) return;
    if (!canManage) return;
    autoClosingRegistrationRef.current.add(selectedTournament.id);
    void adminSetTournamentRegistration(selectedTournament.id, 'close').catch(() => {
      autoClosingRegistrationRef.current.delete(selectedTournament.id);
    });
  }, [selectedTournament?.id, selectedTournament?.status, selectedTournament?.registrationClosesAt, clockNow, canManage]);

  const openTournamentEditor = () => {
    if (!selectedTournament || !canEditTournament) return;

    const tournament = selectedTournament;
    setTournamentMenuOpen(false);
    setSelectedTournament(null);
    setManagementOpen(false);
    setEditingDraft(tournament);
    setWizardOpen(true);
  };

  const primaryAction = selectedTournament ? (() => {
    if (['active', 'live'].includes(selectedStatus)) {
      if (config.submissionEnabled && selectedTournament.verifiedSubmissionsEnabled !== false && myParticipant?.status === 'approved') {
        return { label: 'Log tournament catch', onClick: () => onLogTournamentCatch(selectedTournament), disabled: false };
      }
      if (myParticipant?.status === 'pending') return { label: 'Awaiting approval', onClick: () => {}, disabled: true };
      return { label: 'Watch live tournament', onClick: () => {}, disabled: true };
    }
    if (selectedStatus === 'registration_open') {
      if (myParticipant?.status === 'pending') return { label: 'Awaiting approval', onClick: () => {}, disabled: true };
      if (myParticipant?.status === 'approved') return null;
      return null;
    }
    if (['finalized', 'completed'].includes(selectedStatus)) return { label: 'Tournament finished', onClick: () => {}, disabled: true };
    return null;
  })() : null;

  const shareSelected = async () => {
    if (!selectedTournament) return;
    const result = await share({
      url: buildTournamentShareUrl(selectedTournament.id),
    });
    if (result.ok && result.method === 'clipboard') addToast('Tournament link copied.', 'success');
    else if (!result.ok) addToast('Could not share that tournament.', 'error');
  };

  const managementSection = selectedTournament && canManage ? (
              <section>
                <button
                  type="button"
                  onClick={() => setManagementOpen((value) => !value)}
                  className="flex min-h-12 w-full items-center justify-between gap-3 rounded-[16px] border border-[#2F80ED]/15 bg-[#2F80ED]/8 px-4 py-3 text-left dark:border-[#7057E8]/15 dark:bg-[#7057E8]/10"
                >
                  <div>
                    <div className="flex items-center gap-2 text-sm font-black">
                      {needsResolution ? 'Resolve Tournament' : 'Manage Tournament'}
                      {adminOverride ? <span className="rounded-full bg-amber-400/15 px-2 py-1 text-[9px] uppercase tracking-[.08em] text-amber-800 dark:text-amber-200">Roddo admin override</span> : null}
                    </div>
                    <div className="mt-0.5 text-[11px] font-semibold opacity-55">{needsResolution ? 'Review catches, calculate standings, handle disputes, and finalize results' : 'Operations, review, results, disputes, and payouts'}</div>
                  </div>
                  <span className={`text-xl transition ${managementOpen ? 'rotate-90' : ''}`}>›</span>
                </button>

                {managementOpen ? (
                  <div className="mt-3 space-y-4 rounded-[18px] border border-[var(--tournament-border)] bg-[var(--tournament-muted)] p-3">
                    {['approved', 'registration_open'].includes(selectedStatus) ? (
                      <div>
                        <div className="mb-2 text-xs font-black uppercase tracking-[.12em] opacity-55">Registration</div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={registrationBusyId !== null}
                            onClick={async () => {
                              const actionKey = `registration:${selectedTournament.id}:${selectedStatus === 'registration_open' ? 'close' : 'open'}`;
                              if (!acquireActionLock(actionKey)) return;
                              setRegistrationBusyId(selectedTournament.id);
                              try {
                                await adminSetTournamentRegistration(selectedTournament.id, selectedStatus === 'registration_open' ? 'close' : 'open');
                                addToast(selectedStatus === 'registration_open' ? 'Registration closed.' : 'Registration opened.', 'success');
                              } catch (error: any) {
                                addToast(error?.message || 'Could not update registration.', 'error');
                              } finally {
                                setRegistrationBusyId(null);
                                releaseActionLock(actionKey);
                              }
                            }}
                            className="rounded-xl bg-white/[.07] px-3 py-2 text-xs font-black disabled:opacity-45"
                          >
                            {selectedStatus === 'registration_open' ? 'Close registration' : 'Open registration'}
                          </button>
                        </div>
                        <div className="mt-3">
                          <TournamentParticipantsPanel tournament={selectedTournament} userProfile={userProfile} canManageParticipants registrationEnabled={config.registrationEnabled} addToast={addToast} onOpenUserProfile={onOpenUserProfile} compact />
                        </div>
                      </div>
                    ) : null}

                    {!['finalized', 'completed'].includes(selectedStatus) ? (
                      <div className="border-t border-[var(--tournament-border)] pt-4">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <div className="text-xs font-black uppercase tracking-[.12em] opacity-55">Submission review</div>
                          <span className="text-[10px] font-black opacity-45">{pendingReviewCount} pending</span>
                        </div>
                        <div className="space-y-2">
                          {reviewSubmissions.filter((submission) => submission.status !== 'withdrawn').map((submission) => (
                            <div key={submission.id} className="rounded-[14px] border border-[var(--tournament-border)] bg-[var(--tournament-surface)] p-3">
                              <div className="flex gap-3">
                                {submission.photoUrls?.[0] ? <img src={submission.photoUrls[0]} alt="Tournament catch evidence" className="h-16 w-16 shrink-0 rounded-xl object-cover" /> : null}
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-sm font-black">{submission.participantName} · {submission.species}</div>
                                  <div className="mt-1 text-[11px] font-semibold opacity-55">{submission.measurementValue != null && submission.measurementUnit ? `${submission.measurementValue} ${submission.measurementUnit} · ` : ''}{submission.status.replaceAll('_', ' ')}</div>
                                  {submission.status === 'submitted' ? (
                                    <div className="mt-2 flex gap-2">
                                      <button type="button" disabled={submissionBusyKey !== null} onClick={async () => {
                                        const actionKey = `accept:${submission.id}`;
                                        if (!acquireActionLock(actionKey)) return;
                                        setSubmissionBusyKey(actionKey);
                                        try { await reviewTournamentSubmission(selectedTournament.id, submission.id, 'accept'); addToast('Tournament catch accepted.', 'success'); }
                                        catch (error: any) { addToast(error?.message || 'Could not accept catch.', 'error'); }
                                        finally { setSubmissionBusyKey(null); releaseActionLock(actionKey); }
                                      }} className="rounded-lg bg-emerald-400/10 px-2.5 py-1.5 text-[11px] font-black text-emerald-700 dark:text-emerald-200 disabled:opacity-45">Accept</button>
                                      <button type="button" disabled={submissionBusyKey !== null} onClick={async () => {
                                        const notes = window.prompt('Why are you rejecting this tournament catch?', '') || '';
                                        if (notes.trim().length < 3) return;
                                        const actionKey = `reject:${submission.id}`;
                                        if (!acquireActionLock(actionKey)) return;
                                        setSubmissionBusyKey(actionKey);
                                        try { await reviewTournamentSubmission(selectedTournament.id, submission.id, 'reject', notes); addToast('Tournament catch rejected.', 'info'); }
                                        catch (error: any) { addToast(error?.message || 'Could not reject catch.', 'error'); }
                                        finally { setSubmissionBusyKey(null); releaseActionLock(actionKey); }
                                      }} className="rounded-lg px-2.5 py-1.5 text-[11px] font-black text-[#EF8B91] hover:bg-red-500/10 disabled:opacity-45">Reject</button>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          ))}
                          {!reviewSubmissions.length ? <div className="text-sm font-semibold opacity-50">No catches waiting for review.</div> : null}
                        </div>
                      </div>
                    ) : null}

                    {['submissions_closed', 'under_review', 'provisional_results'].includes(selectedStatus) ? (
                      <div className="border-t border-[var(--tournament-border)] pt-4">
                        <div className="text-xs font-black uppercase tracking-[.12em] opacity-55">Results control</div>
                        {selectedStatus === 'provisional_results' ? (
                          <div className="mt-2 rounded-xl bg-sky-400/10 px-3 py-2 text-xs font-semibold">
                            {disputeWindowOpen ? `Dispute window open until ${formatTournamentDate(disputeWindowEndsAt)}.` : 'Dispute window closed. Results can be finalized.'}
                          </div>
                        ) : null}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" disabled={resultsBusy !== null || pendingReviewCount > 0} onClick={async () => {
                            const actionKey = `results:calculate:${selectedTournament.id}`;
                            if (!acquireActionLock(actionKey)) return;
                            setResultsBusy('calculate');
                            try { await calculateTournamentStandings(selectedTournament.id); addToast('Tournament standings calculated.', 'success'); }
                            catch (error: any) { addToast(error?.message || 'Could not calculate standings.', 'error'); }
                            finally { setResultsBusy(null); releaseActionLock(actionKey); }
                          }} className="rounded-xl bg-[#2F80ED]/10 px-3 py-2 text-xs font-black text-[#2168C9] dark:bg-[#7057E8]/12 dark:text-violet-200 disabled:opacity-45">{resultsBusy === 'calculate' ? 'Calculating…' : 'Calculate standings'}</button>
                          <button type="button" disabled={resultsBusy !== null || pendingReviewCount > 0 || !canFinalize} onClick={async () => {
                            const actionKey = `results:finalize:${selectedTournament.id}`;
                            if (!acquireActionLock(actionKey)) return;
                            setResultsBusy('finalize');
                            try { await finalizeTournamentResults(selectedTournament.id); addToast('Tournament results finalized.', 'success'); }
                            catch (error: any) { addToast(error?.message || 'Could not finalize results.', 'error'); }
                            finally { setResultsBusy(null); releaseActionLock(actionKey); }
                          }} className="rounded-xl bg-emerald-500 px-3 py-2 text-xs font-black text-white disabled:opacity-45">{resultsBusy === 'finalize' ? 'Finalizing…' : disputeWindowOpen && !isAdmin ? 'Finalize after dispute window' : 'Finalize results'}</button>
                        </div>
                      </div>
                    ) : null}

                    {selectedStatus === 'provisional_results' ? (
                      <div className="border-t border-[var(--tournament-border)] pt-4">
                        <div className="text-xs font-black uppercase tracking-[.12em] opacity-55">Disputes</div>
                        <div className="mt-2 space-y-2">
                          {Object.values(tournamentParticipants).filter((participant) => ['approved', 'disqualified'].includes(String(participant.status || ''))).map((participant) => {
                            const disqualified = participant.status === 'disqualified';
                            return (
                              <div key={participant.uid} className="flex items-center justify-between gap-3 rounded-xl border border-[var(--tournament-border)] bg-[var(--tournament-surface)] px-3 py-2">
                                <div className="min-w-0"><div className="truncate text-sm font-black">{participant.displayName}</div><div className="text-[10px] font-semibold opacity-50">{disqualified ? 'Disqualified' : 'Eligible'}</div></div>
                                <button type="button" disabled={submissionBusyKey !== null} onClick={async () => {
                                  const action = disqualified ? 'reinstate' : 'disqualify';
                                  const reason = window.prompt(`${disqualified ? 'Why are you reinstating' : 'Why are you disqualifying'} ${participant.displayName}?`, '') || '';
                                  if (reason.trim().length < 5) return;
                                  const actionKey = `dispute:${participant.uid}:${action}`;
                                  if (!acquireActionLock(actionKey)) return;
                                  setSubmissionBusyKey(actionKey);
                                  try { await resolveTournamentParticipantDispute(selectedTournament.id, participant.uid, action, reason); addToast(disqualified ? 'Participant reinstated. Standings recalculated.' : 'Participant disqualified. Standings recalculated.', 'success'); }
                                  catch (error: any) { addToast(error?.message || 'Could not resolve dispute.', 'error'); }
                                  finally { setSubmissionBusyKey(null); releaseActionLock(actionKey); }
                                }} className={`rounded-lg px-2.5 py-1.5 text-[11px] font-black ${disqualified ? 'bg-emerald-400/10 text-emerald-700 dark:text-emerald-200' : 'text-[#EF8B91] hover:bg-red-500/10'}`}>{disqualified ? 'Reinstate' : 'Disqualify'}</button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    {Number(selectedTournament.entryFeeShells || 0) > 0 && ['finalized', 'completed'].includes(selectedStatus) ? (
                      <div className="border-t border-[var(--tournament-border)] pt-4">
                        <div className="text-xs font-black uppercase tracking-[.12em] opacity-55">Shell payout</div>
                        <p className="mt-1 text-sm font-semibold opacity-65">Pool: {selectedTournament.shellPrizePool || 0} Shells · {String(selectedTournament.payoutStatus || 'pending').replaceAll('_', ' ')}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {isAdmin && selectedTournament.payoutStatus !== 'paid' && selectedTournament.payoutStatus !== 'refunded' ? (
                            <button type="button" disabled={resultsBusy !== null} onClick={async () => {
                              const actionKey = `results:repair:${selectedTournament.id}`;
                            if (!acquireActionLock(actionKey)) return;
                            setResultsBusy('repair');
                              try { const result = await repairTournamentResults(selectedTournament.id); addToast(`Final results rebuilt: ${result.leaderboardCount} leaderboard row${result.leaderboardCount === 1 ? '' : 's'}.`, 'success'); }
                              catch (error: any) { addToast(error?.message || 'Could not rebuild final results.', 'error'); }
                              finally { setResultsBusy(null); releaseActionLock(actionKey); }
                            }} className="rounded-xl bg-amber-400/10 px-3 py-2 text-xs font-black text-amber-800 dark:text-amber-200 disabled:opacity-45">{resultsBusy === 'repair' ? 'Rebuilding…' : 'Rebuild final results'}</button>
                          ) : null}
                          {selectedTournament.payoutStatus !== 'paid' && selectedTournament.payoutStatus !== 'refunded' ? (
                            <button type="button" disabled={resultsBusy !== null} onClick={async () => {
                              const actionKey = `results:payout:${selectedTournament.id}`;
                            if (!acquireActionLock(actionKey)) return;
                            setResultsBusy('payout');
                              try { const result = await settleTournamentPayouts(selectedTournament.id); addToast(result.payoutStatus === 'refunded' ? 'No qualifying winner — entry Shells refunded.' : `${result.paidShells} Shells paid.`, 'success'); }
                              catch (error: any) { addToast(error?.message || 'Could not settle Shell prizes.', 'error'); }
                              finally { setResultsBusy(null); releaseActionLock(actionKey); }
                            }} className="rounded-xl bg-emerald-500 px-3 py-2 text-xs font-black text-white disabled:opacity-45">{resultsBusy === 'payout' ? 'Paying…' : 'Pay Shell prizes'}</button>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </section>
  ) : null;

  const detailsPanelContent = selectedTournament ? (
      <div
        className="flex h-full max-h-full min-h-0 w-full flex-col overflow-hidden"
        style={{
          background: ui.pageBg,
          color: ui.textPrimary,
          ['--tournament-surface' as any]: ui.cardBg,
          ['--tournament-muted' as any]: ui.cardBgMuted,
          ['--tournament-raised' as any]: ui.cardBgStrong,
          ['--tournament-border' as any]: ui.border,
          ['--tournament-border-strong' as any]: ui.borderStrong,
          ['--tournament-accent' as any]: tournamentTone.accent,
          ['--tournament-accent-soft' as any]: tournamentTone.accentSoft,
          ['--tournament-text-muted' as any]: ui.textMuted,
        }}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tournament-details-title"
      >
        <div className="sticky top-0 z-20 shrink-0 rounded-t-[23px] border-b border-[var(--tournament-border)] bg-[var(--tournament-surface)]/95 px-4 py-3 backdrop-blur-xl lg:static">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[.12em] ${statusTone(selectedTournament)}`}>
                {['active', 'live'].includes(selectedStatus) ? '● ' : ''}{statusLabel(selectedTournament, clockNow)}
              </span>
              {selectedTournament.participationMode === 'team' ? (
                <span className="rounded-full bg-amber-400/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[.1em] text-amber-800 dark:text-amber-200">Duo</span>
              ) : null}
              {isRegisteredCompetitor ? (
                <span className="rounded-full border border-emerald-500/25 bg-emerald-400/12 px-2.5 py-1 text-[10px] font-black uppercase tracking-[.1em] text-emerald-700 dark:border-emerald-300/20 dark:bg-emerald-400/10 dark:text-[#8CF0CB]">
                  ✓ Registered
                </span>
              ) : isRegistrationPending ? (
                <span className="rounded-full border border-amber-500/20 bg-amber-400/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[.1em] text-amber-800 dark:text-amber-200">Pending</span>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button type="button" onClick={() => void shareSelected()} className="grid h-10 w-10 place-items-center rounded-full hover:bg-black/[.05] dark:hover:bg-white/[.06]" aria-label="Share tournament" title="Share tournament">
                <ShareIcon className="h-[18px] w-[18px]" />
              </button>
              <button type="button" onClick={closeSelectedTournament} className="grid h-10 w-10 place-items-center rounded-full text-xl font-bold hover:bg-black/[.05] dark:hover:bg-white/[.06]" aria-label="Close tournament" title="Close">×</button>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 touch-pan-y overflow-x-hidden overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
          <section className="border-b border-[var(--tournament-border)] bg-[var(--tournament-surface)]">
            {selectedTournament.coverImageUrl ? (
              <div className={`relative mx-3 mt-3 overflow-hidden rounded-[16px] border border-[var(--tournament-border)] shadow-[0_8px_22px_rgba(0,0,0,.08)] dark:shadow-[0_10px_26px_rgba(0,0,0,.20)] ${liveMode ? 'h-[180px] sm:h-[200px] lg:h-[176px]' : 'h-[210px] sm:h-[240px] lg:h-[196px]'}`}>
                <img
                  src={selectedTournament.coverImageUrl}
                  alt={`${selectedTournament.name} tournament cover`}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{
                    objectPosition: `${Math.min(100, Math.max(0, Number(selectedTournament.coverImagePositionX ?? 50)))}% ${Math.min(100, Math.max(0, Number(selectedTournament.coverImagePositionY ?? 50)))}%`,
                    transform: `scale(${Math.min(1.6, Math.max(1, Number(selectedTournament.coverImageZoom || 1)))})`,
                    transformOrigin: `${Math.min(100, Math.max(0, Number(selectedTournament.coverImagePositionX ?? 50)))}% ${Math.min(100, Math.max(0, Number(selectedTournament.coverImagePositionY ?? 50)))}%`,
                  }}
                />
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(5,7,15,.04)_0%,rgba(5,7,15,.02)_62%,rgba(5,7,15,.24)_100%)]" />
              </div>
            ) : null}

            <div className="px-3 py-3.5 sm:px-4 sm:py-4">
              <div className="relative overflow-visible rounded-[20px] border border-[var(--tournament-border)] bg-[var(--tournament-muted)] p-4 shadow-[0_8px_22px_rgba(0,0,0,.06)] dark:shadow-[0_10px_26px_rgba(0,0,0,.16)]">
                {!selectedTournament.coverImageUrl ? (
                  <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[var(--tournament-accent-soft)] blur-3xl" />
                ) : null}

                <div className="relative">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h2 id="tournament-details-title" className={`font-black leading-[1.04] tracking-[-.035em] ${liveMode ? 'text-[22px] sm:text-[26px]' : 'text-[25px] sm:text-[30px]'}`}>
                        {selectedTournament.name}
                      </h2>
                      <p className="mt-2 text-sm font-bold opacity-70">
                        {selectedTournament.locationLabel || selectedTournament.location || (isWorldwideTournament(selectedTournament) ? 'Worldwide' : 'Location not specified')}
                      </p>
                      <p className="mt-1 text-sm font-black text-[var(--tournament-accent)]">
                        {countdownLabel(selectedTournament, clockNow)}
                      </p>
                    </div>

                    {canEditTournament ? (
                      <div className="relative shrink-0">
                        <button
                          type="button"
                          onClick={() => setTournamentMenuOpen((open) => !open)}
                          className="grid h-10 w-10 place-items-center rounded-full bg-black/[.045] text-[22px] font-black leading-none transition hover:bg-black/[.075] dark:bg-white/[.07] dark:hover:bg-white/[.11]"
                          aria-label="Tournament options"
                          aria-expanded={tournamentMenuOpen}
                          title="Tournament options"
                        >
                          ···
                        </button>
                        {tournamentMenuOpen ? (
                          <div className="absolute right-0 top-11 z-30 w-44 overflow-hidden rounded-[14px] border border-[var(--tournament-border-strong)] bg-[var(--tournament-surface)] p-1.5 text-[var(--tournament-text)] shadow-[0_14px_36px_rgba(0,0,0,.28)]">
                            <button
                              type="button"
                              onClick={openTournamentEditor}
                              className="w-full rounded-[10px] px-3 py-2.5 text-left text-xs font-black hover:bg-black/[.05] dark:hover:bg-white/[.06]"
                            >
                              Edit tournament
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  <div className={`${liveMode ? 'mt-3' : 'mt-4'} grid grid-cols-4 gap-2`}>
                    {[
                      ['Anglers', displayApprovedParticipantCount],
                      ['Catches', acceptedSubmissions.length],
                      ['Shells', Number(selectedTournament.entryFeeShells || 0)],
                      ['Scoring', scoringLabel(selectedTournament)],
                    ].map(([label, value]) => (
                      <div key={String(label)} className="min-w-0 rounded-[14px] border border-[var(--tournament-border)] bg-[var(--tournament-surface)] px-2 py-2.5 text-center">
                        <div className="truncate text-[13px] font-black">{String(value)}</div>
                        <div className="mt-0.5 truncate text-[9px] font-black uppercase tracking-[.08em] opacity-45">{label}</div>
                      </div>
                    ))}
                  </div>

                  {primaryAction && !liveMode ? (
                    <button
                      type="button"
                      onClick={primaryAction.onClick}
                      disabled={primaryAction.disabled}
                      className="mt-3.5 w-full rounded-[15px] bg-[linear-gradient(180deg,#FFD96A_0%,#FFC94A_55%,#F2B53A_100%)] px-4 py-3.5 text-sm font-black uppercase tracking-[.035em] text-[#2F2410] shadow-[0_8px_24px_rgba(181,126,24,.18)] disabled:cursor-default disabled:bg-none disabled:bg-black/[.055] disabled:text-current disabled:shadow-none dark:disabled:bg-white/[.07]"
                    >
                      {primaryAction.label}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </section>

          <div className={`${liveMode ? 'space-y-5' : 'space-y-7'} px-4 py-5 pb-[calc(24px+env(safe-area-inset-bottom,0px))]`}>
            {selectedHistorical ? managementSection : null}

            {liveMode ? (
              <>
                <section
                  className={`overflow-hidden rounded-[20px] border p-4 shadow-[0_10px_26px_rgba(0,0,0,.07)] ${isRegisteredCompetitor
                    ? 'border-emerald-500/25 bg-emerald-400/[.08] dark:border-emerald-300/18 dark:bg-emerald-400/[.07]'
                    : 'border-[#2F80ED]/18 bg-[#2F80ED]/[.055] dark:border-[#7057E8]/20 dark:bg-[#7057E8]/[.07]'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`text-[10px] font-black uppercase tracking-[.17em] ${isRegisteredCompetitor ? 'text-emerald-700 dark:text-[#78E6B0]' : 'text-[#2168C9] dark:text-[#A99AF7]'}`}>
                        {isRegisteredCompetitor ? '● You’re competing live' : '● Live now'}
                      </div>
                      <div className="mt-1 text-[20px] font-black tracking-[-.025em]">
                        {isRegisteredCompetitor ? 'Fish it. Log it. Move the board.' : 'Watch the board move in real time.'}
                      </div>
                      <div className="mt-1.5 text-xs font-semibold leading-5 opacity-60">
                        {isRegisteredCompetitor
                          ? `${endsInLabel || 'Tournament in progress'} · Every accepted catch can change the standings.`
                          : `${endsInLabel || 'Tournament in progress'} · See what anglers are landing, then get into the next one.`}
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[.1em] ${isRegisteredCompetitor ? 'bg-emerald-500 text-white' : 'bg-[#2F80ED] text-white dark:bg-[#7057E8]'}`}>
                      {isRegisteredCompetitor ? 'IN' : 'WATCHING'}
                    </span>
                  </div>

                  {isRegisteredCompetitor && config.submissionEnabled && selectedTournament.verifiedSubmissionsEnabled !== false ? (
                    <button
                      type="button"
                      onClick={() => onLogTournamentCatch(selectedTournament)}
                      className="mt-4 w-full rounded-[15px] bg-[linear-gradient(180deg,#FFD96A_0%,#FFC94A_55%,#F2B53A_100%)] px-4 py-3.5 text-sm font-black uppercase tracking-[.045em] text-[#2F2410] shadow-[0_10px_26px_rgba(181,126,24,.20)] transition active:scale-[.99]"
                    >
                      + Log a tournament fish
                    </button>
                  ) : !isRegistrationPending ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTournament(null);
                        setManagementOpen(false);
                        setTournamentMenuOpen(false);
                        setHistoryView('current');
                      }}
                      className="mt-4 w-full rounded-[15px] border border-[#2F80ED]/20 bg-[#2F80ED]/10 px-4 py-3 text-xs font-black uppercase tracking-[.045em] text-[#2168C9] transition active:scale-[.99] dark:border-[#7057E8]/25 dark:bg-[#7057E8]/12 dark:text-[#CFC7FF]"
                    >
                      Find your next tournament
                    </button>
                  ) : null}
                </section>

                {managementSection}

                <section>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-[.16em] text-emerald-700 dark:text-[#78E6B0]">Live competition</div>
                      <div className="mt-1 text-lg font-black">{isRegisteredCompetitor ? 'Your position' : 'Live pulse'}</div>
                    </div>
                    <div className="rounded-full border border-emerald-500/30 bg-emerald-400/22 px-2.5 py-1 text-[10px] font-black uppercase tracking-[.1em] text-emerald-800 shadow-[0_3px_10px_rgba(16,185,129,.10)] dark:border-emerald-300/24 dark:bg-emerald-400/18 dark:text-[#78E6B0]">● Live</div>
                  </div>

                  <div className="rounded-[18px] border border-[var(--tournament-border)] bg-[var(--tournament-muted)] p-3">
                    {myParticipant?.status === 'approved' ? (
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="text-[11px] font-black uppercase tracking-[.12em] opacity-45">Current standing</div>
                          <div className="mt-1 flex items-baseline gap-2">
                            <span className="text-[30px] font-black leading-none">
                              {myLiveStanding ? `#${myLiveStanding.placement}` : '—'}
                            </span>
                            <span className="text-sm font-black opacity-70">
                              {myLiveStanding
                                ? `${compactNumber(myLiveStanding.score)} ${myLiveStanding.scoreUnit}`
                                : 'No accepted score yet'}
                            </span>
                          </div>
                          <div className="mt-2 text-[11px] font-semibold opacity-55">
                            {myAcceptedSubmissions.length} accepted
                            {myPendingSubmissions.length
                              ? ` · ${myPendingSubmissions.length} pending review`
                              : ''}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-[10px] font-black uppercase tracking-[.1em] opacity-40">Scoring</div>
                          <div className="mt-1 max-w-[120px] text-sm font-black">{scoringLabel(selectedTournament)}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm font-semibold opacity-65">
                        {myParticipant?.status === 'pending'
                          ? 'Your registration is still awaiting approval.'
                          : `${displayApprovedParticipantCount} anglers are competing · ${acceptedSubmissions.length} accepted catches on the board.`}
                      </div>
                    )}
                  </div>
                </section>

              </>
            ) : ['approved', 'registration_open', 'submissions_closed', 'under_review', 'provisional_results', 'finalized', 'completed'].includes(selectedStatus) ? (
              <section>
                {selectedStatus === 'registration_open' && isRegisteredCompetitor ? (
                  <div className="relative mb-4 overflow-hidden rounded-[20px] border border-emerald-500/28 bg-[linear-gradient(135deg,rgba(16,185,129,.13),rgba(47,128,237,.055))] p-4 shadow-[0_10px_28px_rgba(16,185,129,.08)] dark:border-emerald-300/20 dark:bg-[linear-gradient(135deg,rgba(16,185,129,.12),rgba(112,87,232,.08))] dark:shadow-[0_12px_30px_rgba(0,0,0,.16)]">
                    <div className="pointer-events-none absolute -right-8 -top-12 h-28 w-28 rounded-full bg-emerald-400/10 blur-2xl" aria-hidden="true" />
                    <div className="relative flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[.17em] text-emerald-700 dark:text-[#78E6B0]">
                          <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-[11px] text-white shadow-[0_3px_9px_rgba(16,185,129,.22)]">✓</span>
                          Entry locked
                        </div>
                        <div className="mt-2 text-[24px] font-black leading-none tracking-[-.035em]">{startsInLabel || 'Tournament starts soon'}</div>
                        <div className="mt-2 text-xs font-semibold leading-5 opacity-65">Your team is confirmed. When the event goes live, come back here and start logging tournament catches.</div>
                      </div>
                      <span className="shrink-0 rounded-full border border-emerald-500/20 bg-emerald-500/12 px-2.5 py-1 text-[9px] font-black uppercase tracking-[.11em] text-emerald-700 dark:border-emerald-300/18 dark:text-[#8CF0CB]">Ready</span>
                    </div>
                  </div>
                ) : null}
                <div className="mb-3 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[.14em] opacity-45">My tournament</div>
                    <div className="mt-1 text-lg font-black">Status & registration</div>
                  </div>
                </div>
                <div className="rounded-[18px] border border-[var(--tournament-border)] bg-[var(--tournament-muted)] p-3">
                  <TournamentParticipantsPanel
                    tournament={selectedTournament}
                    userProfile={userProfile}
                    canManageParticipants={false}
                    registrationEnabled={config.registrationEnabled}
                    addToast={addToast}
                    onOpenUserProfile={onOpenUserProfile}
                    compact
                  />
                </div>
              </section>
            ) : null}

            {liveMode || ['submissions_closed', 'under_review', 'provisional_results', 'finalized', 'completed'].includes(selectedStatus) ? (
              <section>
                <div className="mb-3 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[.14em] opacity-45">{liveMode ? 'Live board' : 'Competition'}</div>
                    <div className="mt-1 text-lg font-black">{liveMode ? 'Leaderboard · unofficial' : 'Leaderboard'}</div>
                  </div>
                  {liveMode ? (
                    <div className="text-[10px] font-bold opacity-45">Accepted catches only</div>
                  ) : selectedTournament.leaderboardGeneratedAt ? (
                    <div className="text-[10px] font-bold opacity-45">Updated {new Date(selectedTournament.leaderboardGeneratedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
                  ) : null}
                </div>
                <div className="overflow-hidden rounded-[18px] border border-[var(--tournament-border)] bg-[var(--tournament-muted)]">
                  {displayedStandings.length ? displayedStandings.map((entry, index) => (
                    <div key={entry.id} className={`flex items-center justify-between gap-3 px-3 py-3 ${index ? 'border-t border-black/[.06] dark:border-white/[.07]' : ''}`}>
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-black ${entry.placement <= 3 ? 'bg-amber-400/12' : 'bg-black/[.04] dark:bg-white/[.05]'}`}>{placementMark(entry.placement)}</div>
                        <div className="min-w-0">
                          <div className={`truncate text-sm font-black ${liveMode && 'participantUid' in entry && entry.participantUid === userProfile.uid ? 'text-[#2F80ED] dark:text-[#A99AF7]' : ''}`}>{entry.teamName || entry.participantName || 'Tournament angler'}{liveMode && 'participantUid' in entry && entry.participantUid === userProfile.uid ? ' · You' : ''}</div>
                          <div className="mt-0.5 text-[11px] font-semibold opacity-50">{entry.acceptedCatchCount} accepted · {entry.speciesCount} species</div>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-base font-black">{compactNumber(Number(entry.score || 0))}</div>
                        <div className="text-[9px] font-black uppercase tracking-[.09em] opacity-45">{entry.scoreUnit}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="px-3 py-5 text-sm font-semibold opacity-55">{liveMode ? 'No accepted catches are on the live board yet.' : 'Standings have not been calculated yet.'}</div>
                  )}
                </div>
              </section>
            ) : null}

            {['active', 'live', 'submissions_closed', 'under_review', 'provisional_results', 'finalized', 'completed'].includes(selectedStatus) || selectedHistorical ? (
              <section>
                <div className="mb-3 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[.14em] opacity-45">Event feed</div>
                    <div className="mt-1 text-lg font-black">Recent catches</div>
                  </div>
                  <div className="text-[11px] font-black opacity-45">{acceptedSubmissions.length} accepted</div>
                </div>
                {acceptedSubmissions.length ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {acceptedSubmissions.slice(0, liveMode ? 6 : 12).map((submission) => (
                      <article key={submission.id} className="overflow-hidden rounded-[18px] border border-[var(--tournament-border)] bg-[var(--tournament-muted)]">
                        {submission.photoUrls?.[0] ? <img src={submission.photoUrls[0]} alt={`${submission.species} tournament catch`} className={liveMode ? "h-36 w-full object-cover" : "h-44 w-full object-cover"} /> : <div className="grid h-28 place-items-center text-3xl opacity-35">🎣</div>}
                        <div className="p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-black">{submission.participantName || 'Roddo Angler'}</div>
                              <div className="mt-0.5 truncate text-xs font-semibold opacity-60">{submission.species}</div>
                            </div>
                            {submission.measurementValue != null && submission.measurementUnit ? (
                              <div className="shrink-0 text-right">
                                <div className="font-black">{submission.measurementValue} {submission.measurementUnit}</div>
                                <div className="text-[9px] font-black uppercase tracking-[.08em] text-emerald-700 dark:text-[#8CF0CB]">Accepted</div>
                              </div>
                            ) : null}
                          </div>
                          <div className="mt-2 text-[10px] font-semibold opacity-45">{submission.submittedAt ? new Date(submission.submittedAt).toLocaleString() : ''}</div>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[18px] border border-[var(--tournament-border)] bg-[var(--tournament-muted)] px-3 py-5 text-sm font-semibold opacity-55">No accepted catches yet.</div>
                )}
              </section>
            ) : null}

            {liveMode && mySubmissions.length ? (
              <section>
                <div className="mb-2 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[.14em] opacity-45">My tournament</div>
                    <div className="mt-1 text-lg font-black">My catches</div>
                  </div>
                  <div className="text-[11px] font-black opacity-45">{mySubmissions.length} total</div>
                </div>
                <div className="space-y-2">
                  {mySubmissions.slice(0, 8).map((submission) => (
                    <div key={submission.id} className="flex items-center gap-3 rounded-[15px] border border-[var(--tournament-border)] bg-[var(--tournament-muted)] p-2.5">
                      {submission.photoUrls?.[0] ? <img src={submission.photoUrls[0]} alt="" className="h-14 w-14 shrink-0 rounded-[10px] object-cover" /> : <div className="grid h-14 w-14 shrink-0 place-items-center rounded-[10px] bg-black/[.035] text-xl dark:bg-white/[.04]">🎣</div>}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-black">{submission.species}</div>
                        <div className="mt-0.5 text-[11px] font-semibold opacity-50">
                          {submission.measurementValue != null && submission.measurementUnit ? `${submission.measurementValue} ${submission.measurementUnit} · ` : ''}
                          {submission.status.replaceAll('_', ' ')}
                        </div>
                      </div>
                      {submission.status === 'submitted' ? (
                        <button type="button" disabled={submissionBusyKey !== null} onClick={async () => {
                          const actionKey = `withdraw:${submission.id}`;
                          if (!acquireActionLock(actionKey)) return;
                          setSubmissionBusyKey(actionKey);
                          try { await withdrawTournamentSubmission(selectedTournament.id, submission.id); addToast('Tournament catch withdrawn.', 'info'); }
                          catch (error: any) { addToast(error?.message || 'Could not withdraw catch.', 'error'); }
                          finally { setSubmissionBusyKey(null); releaseActionLock(actionKey); }
                        }} className="rounded-lg px-2.5 py-1.5 text-[11px] font-black text-[#EF8B91]">Withdraw</button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <section>
              <details className="group overflow-hidden rounded-[18px] border border-[var(--tournament-border)] bg-[var(--tournament-muted)]">
                <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 [&::-webkit-details-marker]:hidden">
                  <div className="min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-[.14em] opacity-45">Event details</div>
                    <div className="mt-0.5 text-base font-black">Tournament information</div>
                    <div className="mt-0.5 truncate text-[11px] font-semibold opacity-50">Rules, schedule, location, verification, prizes & participants</div>
                  </div>
                  <span className="shrink-0 text-xl opacity-45 transition group-open:rotate-90">›</span>
                </summary>
                <div className="border-t border-[var(--tournament-border)] px-3">
                <Disclosure title="About" defaultOpen={!liveMode}>
                  <p className="whitespace-pre-wrap">{selectedTournament.description || 'No description provided.'}</p>
                </Disclosure>
                <Disclosure title="Rules">
                  <p className="whitespace-pre-wrap">{selectedTournament.rulesText || selectedTournament.rules || 'No rules provided.'}</p>
                  {selectedTournament.allowedMethods?.length ? <p className="mt-3"><span className="font-black">Allowed:</span> {selectedTournament.allowedMethods.join(', ')}</p> : null}
                  {selectedTournament.disallowedMethods?.length ? <p className="mt-2"><span className="font-black">Not allowed:</span> {selectedTournament.disallowedMethods.join(', ')}</p> : null}
                </Disclosure>
                <Disclosure title="Eligible species">
                  {selectedTournament.eligibleSpecies?.length ? selectedTournament.eligibleSpecies.join(', ') : 'Any eligible species'}
                </Disclosure>
                <Disclosure title="Schedule">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div><span className="font-black">Registration:</span> {formatTournamentDate(selectedTournament.registrationOpensAt)} → {formatTournamentDate(selectedTournament.registrationClosesAt)}</div>
                    <div><span className="font-black">Tournament:</span> {formatTournamentDate(selectedTournament.startDate)} → {formatTournamentDate(selectedTournament.endDate)}</div>
                  </div>
                </Disclosure>
                <Disclosure title="Location">
                  {selectedTournament.locationLabel || selectedTournament.location || (isWorldwideTournament(selectedTournament) ? 'Worldwide' : 'Not specified')}
                </Disclosure>
                <Disclosure title="Verification">
                  <div className="space-y-1.5">
                    <div>{selectedTournament.submissionCapturePolicy === 'live_or_upload' ? 'Live camera or uploaded photo' : 'Live camera photo only'}</div>
                    {selectedTournament.lengthBoardRequired ? <div>Length board required</div> : null}
                    {selectedTournament.scaleRequired ? <div>Scale required</div> : null}
                    {selectedTournament.tournamentCodeRequired ? <div>Tournament code required</div> : null}
                    {selectedTournament.tournamentCodeRequired && selectedTournament.tournamentCode && myParticipant?.status === 'approved' ? <div className="mt-2 rounded-xl bg-[#2F80ED]/10 px-3 py-2 font-black dark:bg-[#7057E8]/10">Code: {selectedTournament.tournamentCode}</div> : null}
                  </div>
                </Disclosure>
                <Disclosure title="Entry & prizes">
                  <div className="space-y-1.5">
                    <div>
                      <span className="font-black">Entry:</span>{' '}
                      {Number(selectedTournament.entryFeeShells || 0) > 0
                        ? `${selectedTournament.entryFeeShells} Shells per angler`
                        : 'Free'}
                    </div>
                    <div>
                      <span className="font-black">Current field:</span>{' '}
                      {displayApprovedParticipantCount} angler{(displayApprovedParticipantCount) === 1 ? '' : 's'}
                      {selectedTournament.participationMode === 'team' && approvedDuoCount > 0
                        ? ` · ${approvedDuoCount} complete Duo${approvedDuoCount === 1 ? '' : 's'}`
                        : ''}
                    </div>
                    <div>
                      <span className="font-black">Current prize pool:</span>{' '}
                      {Number(selectedTournament.entryFeeShells || 0) > 0
                        ? `${Number(selectedTournament.shellPrizePool || 0)} Shells`
                        : 'No paid pool'}
                    </div>
                    {Number(selectedTournament.entryFeeShells || 0) > 0 ? (
                      <div className="text-xs font-semibold opacity-55">Grows as paid anglers register.</div>
                    ) : null}
                    <div><span className="font-black">Payout:</span> {prizePayoutLabel}</div>
                  </div>
                </Disclosure>
                <Disclosure title="Participants" summary={selectedTournament.participationMode === 'team' ? `${approvedDuoCount} Duo${approvedDuoCount === 1 ? '' : 's'} · ${displayApprovedParticipantCount} anglers` : `${displayApprovedParticipantCount} approved anglers`}>
                  <TournamentParticipantsPanel tournament={selectedTournament} userProfile={userProfile} canManageParticipants={false} registrationEnabled={config.registrationEnabled} addToast={addToast} onOpenUserProfile={onOpenUserProfile} compact />
                </Disclosure>
                </div>
              </details>
            </section>

            {!selectedHistorical && !liveMode ? managementSection : null}

            {!liveMode && mySubmissions.length ? (
              <section>
                <Disclosure title="My catches" summary={`${mySubmissions.length} submitted`}>
                  <div className="space-y-2">
                    {mySubmissions.map((submission) => (
                      <div key={submission.id} className="flex items-center gap-3 rounded-xl border border-[var(--tournament-border)] bg-[var(--tournament-surface)] p-2.5">
                        {submission.photoUrls?.[0] ? <img src={submission.photoUrls[0]} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" /> : null}
                        <div className="min-w-0 flex-1"><div className="truncate font-black">{submission.species}</div><div className="text-[11px] font-semibold opacity-50">{submission.status.replaceAll('_', ' ')}</div></div>
                        {submission.status === 'submitted' ? <button type="button" disabled={submissionBusyKey !== null} onClick={async () => {
                          const actionKey = `withdraw:${submission.id}`;
                          if (!acquireActionLock(actionKey)) return;
                          setSubmissionBusyKey(actionKey);
                          try { await withdrawTournamentSubmission(selectedTournament.id, submission.id); addToast('Tournament catch withdrawn.', 'info'); }
                          catch (error: any) { addToast(error?.message || 'Could not withdraw catch.', 'error'); }
                          finally { setSubmissionBusyKey(null); releaseActionLock(actionKey); }
                        }} className="rounded-lg px-2.5 py-1.5 text-[11px] font-black text-[#EF8B91]">Withdraw</button> : null}
                      </div>
                    ))}
                  </div>
                </Disclosure>
              </section>
            ) : null}
          </div>
        </div>

      </div>
  ) : null;

  const detailsPopover = selectedTournament ? (
    <div
      className={`${isDarkMode ? 'dark' : ''} fixed left-0 right-0 z-[1500] flex items-center justify-center px-2 py-3 sm:px-4 sm:py-5`}
      style={{
        top: 'var(--roddo-header-h, 72px)',
        bottom: 'var(--roddo-nav-h, 72px)',
        paddingRight: 'max(8px, env(safe-area-inset-right, 0px))',
        paddingLeft: 'max(8px, env(safe-area-inset-left, 0px))',
        background: isDarkMode
          ? 'rgba(5, 10, 20, 0.94)'
          : 'rgba(247, 241, 231, 0.96)',
      }}
      onClick={closeSelectedTournament}
      role="presentation"
    >
      <div
        className="h-[min(70dvh,700px)] min-h-0 w-full max-w-[720px] overflow-hidden rounded-[24px] border border-[var(--tournament-border)] bg-[var(--tournament-surface)] shadow-[0_28px_80px_rgba(0,0,0,.30)] sm:h-[min(74dvh,740px)]"
        style={{
          borderRadius: '24px',
          clipPath: 'inset(0 round 24px)',
          WebkitClipPath: 'inset(0 round 24px)',
          background: ui.pageBg,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {detailsPanelContent}
      </div>
    </div>
  ) : null;

  if (wizardOpen) {
    return (
      <TournamentDraftWizard
        draft={editingDraft}
        userId={userProfile.uid}
        userName={userProfile.name || userProfile.displayName || 'Roddo Director'}
        config={config}
        isAdmin={isAdmin}
        onCancel={() => { setWizardOpen(false); setEditingDraft(null); }}
        onSaved={() => { setWizardOpen(false); setEditingDraft(null); }}
        addToast={addToast}
      />
    );
  }

  return (
    <div
      className="mx-auto w-full max-w-6xl"
    >
      <div className="min-w-0">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[.16em] opacity-45">Roddo Tournaments</div>
          <h1 className="mt-1 text-[24px] font-black tracking-[-.03em]">Tournament Dock</h1>
          <p className="mt-1 text-sm font-semibold opacity-55">Find an event, compete, and follow the live board.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
  {config.creationEnabled ? (
    <button
      type="button"
      onClick={() => {
        setEditingDraft(null);
        setWizardOpen(true);
      }}
      className="rounded-[14px] bg-[#2F80ED] px-3.5 py-2.5 text-xs font-black text-white shadow-[0_6px_16px_rgba(47,128,237,.18)] transition active:scale-[0.98] dark:bg-[#7057E8] dark:shadow-[0_8px_20px_rgba(112,87,232,.20)]"
    >
      Create
    </button>
  ) : null}

  <button
    type="button"
    onClick={onBack}
    aria-label="Back"
    title="Back"
    className="
      grid h-10 w-10 place-items-center rounded-full border
      border-black/[.08] bg-white/70
      text-[22px] font-black leading-none text-slate-700
      shadow-[0_4px_12px_rgba(0,0,0,.06)]
      transition hover:bg-white active:scale-95
      dark:border-white/[.10] dark:bg-white/[.06]
      dark:text-slate-100 dark:hover:bg-white/[.09]
    "
  >
    ‹
  </button>
</div>
      </div>

      {historyView === 'current' && hostQueueRows.length ? (
        <section className="mb-4 overflow-hidden rounded-[18px] border border-black/[.07] bg-white/50 shadow-[0_10px_26px_rgba(42,32,22,.06)] dark:border-white/[.08] dark:bg-[#151427]/88 dark:shadow-[0_12px_28px_rgba(0,0,0,.18)]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/[.06] px-4 py-3 dark:border-white/[.07]">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[.15em] text-[#8A6524] dark:text-[#F3CF74]">Your hosting queue</div>
              <div className="mt-0.5 text-sm font-black">Drafts, revisions & Roddo review</div>
              <div className="mt-0.5 text-[11px] font-semibold opacity-55">Your tournament stays here until it is approved and moves into the live event list.</div>
            </div>
            <span className="grid min-w-8 place-items-center rounded-full border border-amber-400/35 bg-amber-300/18 px-2.5 py-1 text-[10px] font-black text-amber-800 dark:border-amber-300/25 dark:bg-amber-400/12 dark:text-amber-200">{hostQueueRows.length}</span>
          </div>

          <div className="divide-y divide-black/[.055] dark:divide-white/[.06]">
            {hostQueueRows.map((tournament) => {
              const status = String(tournament.status || '');
              const approval = String(tournament.approvalStatus || '');
              const needsRevision = approval === 'changes_requested';
              const pendingReview = status === 'pending_approval' || approval === 'pending';
              const label = needsRevision ? 'Needs revision' : pendingReview ? 'Pending Roddo review' : 'Draft';
              const tone = needsRevision
                ? 'border-red-300/45 bg-red-400/12 text-red-700 dark:border-red-300/25 dark:bg-red-400/10 dark:text-red-200'
                : pendingReview
                  ? 'border-amber-400/45 bg-amber-300/20 text-amber-800 dark:border-amber-300/25 dark:bg-amber-400/12 dark:text-amber-200'
                  : 'border-slate-300/55 bg-slate-200/45 text-slate-700 dark:border-white/10 dark:bg-white/[.06] dark:text-slate-200';
              const note = String((tournament as any).reviewNotes || '').trim();

              return (
                <div key={`host-queue:${tournament.id}`} className="flex items-center gap-3 px-4 py-3">
                  <button type="button" onClick={() => setSelectedTournament(tournament)} className="min-w-0 flex-1 text-left">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2 py-1 text-[9px] font-black uppercase tracking-[.08em] ${tone}`}>{label}</span>
                      {needsRevision ? <span className="text-[10px] font-bold text-red-700/80 dark:text-red-200/80">Action required</span> : null}
                    </div>
                    <div className="mt-1.5 truncate text-sm font-black">{tournament.name}</div>
                    <div className="mt-0.5 truncate text-[11px] font-semibold opacity-55">
                      {needsRevision && note ? `Roddo note: ${note}` : pendingReview ? 'Submitted and waiting for Roddo approval.' : 'Not submitted yet.'}
                    </div>
                  </button>

                  {status === 'draft' ? (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => { setEditingDraft(tournament); setWizardOpen(true); }}
                        className="rounded-[10px] border border-black/[.08] bg-white/70 px-2.5 py-2 text-[10px] font-black shadow-sm hover:bg-white dark:border-white/[.09] dark:bg-white/[.06] dark:hover:bg-white/[.09]"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={submittingId !== null}
                        onClick={async () => {
                          const actionKey = `host-queue-submit:${tournament.id}`;
                          if (!acquireActionLock(actionKey)) return;
                          setSubmittingId(tournament.id);
                          try {
                            await submitTournamentForApproval(tournament.id);
                            addToast(needsRevision ? 'Tournament revision resubmitted for Roddo approval.' : 'Tournament submitted for Roddo approval.', 'success');
                          } catch (error: any) {
                            addToast(error?.message || 'Could not submit tournament.', 'error');
                          } finally {
                            setSubmittingId(null);
                            releaseActionLock(actionKey);
                          }
                        }}
                        className="rounded-[10px] bg-[#2F80ED] px-2.5 py-2 text-[10px] font-black text-white shadow-sm disabled:opacity-45 dark:bg-[#7057E8]"
                      >
                        {submittingId === tournament.id ? 'Sending…' : needsRevision ? 'Resubmit' : 'Submit'}
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setSelectedTournament(tournament)} className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-black/[.035] text-lg opacity-60 dark:bg-white/[.06]">›</button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {historyView === 'current' ? (
        <div className="mb-4">
          <TournamentDiscoveryMap
            tournaments={publicDiscoveryRows}
            isDarkMode={isDarkMode}
            onOpenTournament={setSelectedTournament}
            onSearchArea={setTournamentMapBounds}
            areaFilterActive={!!tournamentMapBounds}
            onClearArea={() => setTournamentMapBounds(null)}
          />
        </div>
      ) : null}

      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex rounded-[12px] bg-black/[.035] p-1 dark:bg-white/[.04]">
          {(['current', 'resolve', 'past'] as const).map((view) => (
            <button
              key={view}
              type="button"
              onClick={() => setHistoryView(view)}
              className={`flex items-center gap-1.5 rounded-[9px] px-3 py-2 text-xs font-black capitalize ${historyView === view ? 'bg-white shadow-sm dark:bg-[#182238]' : 'opacity-50'}`}
            >
              <span>{view}</span>
              {view === 'resolve' && resolveQueueRows.length ? (
                <span className="grid min-w-5 place-items-center rounded-full bg-amber-400/18 px-1.5 py-0.5 text-[9px] font-black text-amber-800 dark:bg-amber-300/12 dark:text-amber-200">
                  {resolveQueueRows.length}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        {historyView === 'current' ? (
          <button type="button" onClick={() => setDiscoveryFiltersOpen((open) => !open)} className="rounded-[11px] px-3 py-2 text-xs font-black opacity-70 hover:bg-black/[.04] dark:hover:bg-white/[.04]">Filters{filtersActive ? ' · on' : ''}</button>
        ) : null}
      </div>


      {historyView === 'resolve' ? (
        <div className="mb-4 rounded-[16px] border border-amber-400/25 bg-amber-300/10 px-4 py-3 dark:border-amber-300/18 dark:bg-amber-400/[.07]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[.15em] text-amber-800 dark:text-amber-200">Resolve tournaments</div>
              <div className="mt-1 text-sm font-black">Finish the events you hosted</div>
              <div className="mt-1 text-xs font-semibold opacity-60">Review remaining catches, calculate standings, handle disputes, and finalize results. Finalized tournaments move to Past automatically.</div>
            </div>
            <span className="grid min-w-8 place-items-center rounded-full border border-amber-400/30 bg-white/45 px-2.5 py-1 text-[10px] font-black text-amber-800 dark:border-amber-300/20 dark:bg-white/[.04] dark:text-amber-200">
              {resolveQueueRows.length}
            </span>
          </div>
        </div>
      ) : null}

      {historyView === 'current' && discoveryFiltersOpen ? (
        <div className="mb-4 rounded-[16px] bg-black/[.025] p-3 dark:bg-white/[.035]">
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-xs font-black">Mode<select value={discoveryFilters.mode} onChange={(event) => setDiscoveryFilters((prev) => ({ ...prev, mode: event.target.value as TournamentDiscoveryFilters['mode'] }))} className="mt-1.5 w-full rounded-xl border border-black/10 bg-transparent px-2.5 py-2 text-sm dark:border-white/10"><option value="all">All</option><option value="solo">Solo</option><option value="team">Duo</option></select></label>
            <label className="flex items-center gap-2 text-xs font-black"><input type="checkbox" checked={discoveryFilters.freeOnly} onChange={(event) => setDiscoveryFilters((prev) => ({ ...prev, freeOnly: event.target.checked }))} />Free only</label>
            <label className="flex items-center gap-2 text-xs font-black"><input type="checkbox" checked={discoveryFilters.hideUnaffordable} onChange={(event) => setDiscoveryFilters((prev) => ({ ...prev, hideUnaffordable: event.target.checked }))} />Hide unaffordable</label>
          </div>
          {filtersActive ? <button type="button" onClick={() => { setDiscoveryFilters(DEFAULT_TOURNAMENT_DISCOVERY_FILTERS); setTournamentMapBounds(null); }} className="mt-3 text-xs font-black text-[#2168C9] dark:text-[#A99AF7]">Clear filters</button> : null}
        </div>
      ) : null}

      <div className="space-y-2.5">
        {rows.length ? rows.map((tournament) => {
          const isDirector = tournament.directorUid === userProfile.uid;
          const date = historyView === 'current' ? tournament.startDate : tournament.endDate;
          const approvedCountRaw = Number(tournament.approvedParticipantCount);
          const hasApprovedCount = Number.isFinite(approvedCountRaw) && approvedCountRaw >= 0;
          const approvedCount = hasApprovedCount ? Math.max(0, Math.round(approvedCountRaw)) : 0;
          const maxCountRaw = Number(tournament.maxParticipants);
          const hasMaxCount = Number.isFinite(maxCountRaw) && maxCountRaw > 0;
          const anglerCountLabel = hasApprovedCount
            ? hasMaxCount
              ? `${approvedCount}/${Math.round(maxCountRaw)} anglers`
              : `${approvedCount} angler${approvedCount === 1 ? '' : 's'}`
            : '';
          const cardCountdown = historyView === 'current' ? tournamentCardCountdown(tournament, clockNow) : '';
          return (
            <button
              key={tournament.id}
              type="button"
              onClick={() => setSelectedTournament(tournament)}
              className={`group relative w-full overflow-hidden rounded-[20px] border text-left shadow-[0_10px_24px_rgba(42,32,22,.08)] transition hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(42,32,22,.12)] dark:shadow-[0_12px_28px_rgba(0,0,0,.22)] ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'min-h-[146px] border-white/10 text-white' : 'border-black/[.07] bg-white/45 dark:border-[rgba(159,140,220,.14)] dark:bg-[#151427]'}`}
            >
              {tournament.coverThumbUrl || tournament.coverImageUrl ? (
                <>
                  <img
                    src={tournament.coverThumbUrl || tournament.coverImageUrl || ''}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition duration-500"
                    style={{
                      objectPosition: `${Math.min(100, Math.max(0, Number(tournament.coverImagePositionX ?? 50)))}% ${Math.min(100, Math.max(0, Number(tournament.coverImagePositionY ?? 50)))}%`,
                      transform: `scale(${Math.min(1.6, Math.max(1, Number(tournament.coverImageZoom || 1)))})`,
                      transformOrigin: `${Math.min(100, Math.max(0, Number(tournament.coverImagePositionX ?? 50)))}% ${Math.min(100, Math.max(0, Number(tournament.coverImagePositionY ?? 50)))}%`,
                    }}
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,7,15,.92)_0%,rgba(5,7,15,.78)_52%,rgba(5,7,15,.54)_100%)]" />
                </>
              ) : (
                <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#2F80ED,#67B7FF,#F2B84B)] dark:bg-[linear-gradient(90deg,#7057E8,#9278EC,#F2B84B)] opacity-70" />
              )}

              <div className={`relative px-4 py-4 ${tournament.coverThumbUrl || tournament.coverImageUrl ? "min-h-[146px] flex flex-col justify-between" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-[.1em] ${tournament.coverThumbUrl || tournament.coverImageUrl ? `${statusToneOnCover(tournament)} backdrop-blur-md` : statusTone(tournament)}`}>{statusLabel(tournament, clockNow)}</span>
                      {isDirector ? <span className={`text-[9px] font-black uppercase tracking-[.08em] ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'text-[#BDEFF5]' : 'text-sky-700 dark:text-sky-200'}`}>Your tournament</span> : null}
                      {historyView === 'resolve' ? <span className={`rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-[.08em] ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'border border-amber-300/25 bg-amber-300/18 text-[#FFE29A] backdrop-blur-md' : 'border border-amber-400/25 bg-amber-300/12 text-amber-800 dark:border-amber-300/20 dark:bg-amber-400/10 dark:text-amber-200'}`}>Needs resolution</span> : null}
                    </div>
                    <div className={`mt-2 truncate text-[17px] font-black tracking-[-.02em] ${tournament.coverThumbUrl || tournament.coverImageUrl ? "[text-shadow:0_2px_9px_rgba(0,0,0,.82)]" : ""}`}>{tournament.name}</div>
                    <div className={`mt-1 truncate text-xs font-semibold ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'text-white/82 [text-shadow:0_1px_6px_rgba(0,0,0,.78)]' : 'opacity-55'}`}>{tournament.locationLabel || tournament.location || (isWorldwideTournament(tournament) ? 'Worldwide' : 'Location not specified')} · {date ? new Date(date).toLocaleDateString() : 'Date TBD'}</div>
                    {cardCountdown ? (
                      <div className={`mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-black ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'border border-white/10 bg-black/36 text-[#FFE29A] backdrop-blur-md [text-shadow:0_1px_5px_rgba(0,0,0,.68)]' : 'bg-amber-400/10 text-amber-800 dark:bg-amber-300/10 dark:text-[#FFE29A]'}`}>
                        <span aria-hidden="true">⏱</span>{cardCountdown}
                      </div>
                    ) : null}
                  </div>
                  <span className={`mt-5 grid h-8 w-8 shrink-0 place-items-center rounded-full text-xl ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'border border-white/10 bg-black/38 text-white/85 backdrop-blur-md' : 'bg-black/[.035] opacity-45 dark:bg-white/[.05]'}`}>›</span>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[.07em] ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'border border-white/10 bg-black/42 text-white/90 backdrop-blur-md' : 'bg-black/[.04] opacity-65 dark:bg-white/[.05]'}`}>{tournament.participationMode === 'team' ? 'Duo' : 'Solo'}</span>
                  <span className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[.07em] ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'border border-white/10 bg-black/42 text-white/90 backdrop-blur-md' : 'bg-black/[.04] opacity-65 dark:bg-white/[.05]'}`}>{scoringLabel(tournament)}</span>
                  {anglerCountLabel ? <span className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[.07em] ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'border border-white/10 bg-black/42 text-white/90 backdrop-blur-md' : 'bg-black/[.04] opacity-65 dark:bg-white/[.05]'}`}>👥 {anglerCountLabel}</span> : null}
                  <span className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[.07em] ${Number(tournament.entryFeeShells || 0) > 0 ? 'border border-[#F2B84B]/25 bg-[#F2B84B]/22 text-[#FFE4A2] backdrop-blur-md' : tournament.coverThumbUrl || tournament.coverImageUrl ? 'border border-white/10 bg-black/42 text-white/90 backdrop-blur-md' : 'bg-black/[.04] opacity-65 dark:bg-white/[.05]'}`}>{Number(tournament.entryFeeShells || 0) > 0 ? `${tournament.entryFeeShells} Shells` : 'Free'}</span>
                </div>

                {isDirector && tournament.status === 'draft' ? (
                  <div className="mt-3 flex gap-2" onClick={(event) => event.stopPropagation()}>
                    <button type="button" onClick={() => { setEditingDraft(tournament); setWizardOpen(true); }} className={`rounded-lg px-2.5 py-1.5 text-[11px] font-black ${tournament.coverThumbUrl || tournament.coverImageUrl ? 'bg-white/12 text-white backdrop-blur-sm' : 'bg-black/[.04] dark:bg-white/[.06]'}`}>Edit</button>
                    <button type="button" disabled={submittingId !== null} onClick={async () => {
                      const actionKey = `submit-for-review:${tournament.id}`;
                      if (!acquireActionLock(actionKey)) return;
                      setSubmittingId(tournament.id);
                      try { await submitTournamentForApproval(tournament.id); addToast('Tournament submitted for Roddo approval.', 'success'); }
                      catch (error: any) { addToast(error?.message || 'Could not submit tournament.', 'error'); }
                      finally { setSubmittingId(null); releaseActionLock(actionKey); }
                    }} className="rounded-lg bg-[#2F80ED] px-2.5 py-1.5 text-[11px] font-black text-white backdrop-blur-sm disabled:opacity-45 dark:bg-[#7057E8]/90">{submittingId === tournament.id ? 'Submitting…' : 'Submit for review'}</button>
                  </div>
                ) : null}
              </div>
            </button>
          );
        }) : historyView === 'past' && isPastLoading ? null : (
          <div className="rounded-[18px] bg-black/[.025] px-4 py-7 dark:bg-[#121A2B]">
            <div className="font-black">
              {historyView === 'past'
                ? 'No past tournaments yet.'
                : historyView === 'resolve'
                  ? 'All caught up.'
                  : filtersActive
                    ? 'No tournaments match these filters.'
                    : 'No current tournaments are available.'}
            </div>
            <div className="mt-1 text-sm font-semibold opacity-55">
              {historyView === 'past'
                ? 'Finalized, completed, cancelled, rejected, and archived events will appear here.'
                : historyView === 'resolve'
                  ? 'Hosted tournaments that still need review or final results will appear here.'
                  : 'Adjust filters, move the map, or create a new event.'}
            </div>
          </div>
        )}

        {historyView === 'past' && isPastLoading && !rows.length ? (
          <div className="rounded-[18px] bg-black/[.025] px-4 py-7 text-center text-sm font-semibold opacity-60 dark:bg-[#121A2B]">
            Loading tournament history…
          </div>
        ) : null}

        {historyView === 'past' && hasMorePast ? (
          <div className="pt-2 text-center">
            <button
              type="button"
              onClick={() => void loadMorePast()}
              disabled={isPastLoadingMore}
              className="min-w-[150px] rounded-[13px] border border-black/[.08] bg-white/70 px-4 py-2.5 text-xs font-black shadow-sm transition hover:bg-white disabled:cursor-wait disabled:opacity-55 dark:border-white/[.10] dark:bg-white/[.06] dark:hover:bg-white/[.09]"
            >
              {isPastLoadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </div>
      </div>

      {detailsPopover}
    </div>
  );
}
